import { useCallback, useEffect, useState } from 'react';
import type {
    OrthopulseShipToDefaults,
    ShipToTarget
} from '../../../../../services/orthopulseOrder';
import {
    EMPTY_SHIP_TO_FORM,
    patientDefaultsToForm,
    practiceDefaultsToForm,
    type ShipToFormValues
} from './shipToTypes';

interface UseShipToFormsResult {
    target: ShipToTarget;
    setTarget: (target: ShipToTarget) => void;
    values: ShipToFormValues;
    setValues: (values: ShipToFormValues) => void;
    /** Raw stored state/province that couldn't be matched to a known option -
     *  ShipToForm shows this until the operator picks a replacement. */
    unmatchedState: string | null;
}

const EMPTY_UNMATCHED_STATE: Record<ShipToTarget, string | null> = {
    practice: null,
    patient: null
};

// Keeps one form per target so switching practice <-> patient swaps to that
// target's edits without losing the other's. Seeds both from the backend
// defaults exactly once, the first time they arrive.
export function useShipToForms(
    defaults: OrthopulseShipToDefaults | undefined
): UseShipToFormsResult {
    const [target, setTarget] = useState<ShipToTarget>('practice');
    const [seeded, setSeeded] = useState(false);
    const [formsByTarget, setFormsByTarget] = useState<Record<ShipToTarget, ShipToFormValues>>({
        practice: EMPTY_SHIP_TO_FORM,
        patient: EMPTY_SHIP_TO_FORM
    });
    const [unmatchedStateByTarget, setUnmatchedStateByTarget] =
        useState<Record<ShipToTarget, string | null>>(EMPTY_UNMATCHED_STATE);

    useEffect(() => {
        if (seeded || !defaults) return;
        const practice = practiceDefaultsToForm(defaults.practice);
        const patient = patientDefaultsToForm(defaults.patient);
        setFormsByTarget({ practice: practice.values, patient: patient.values });
        setUnmatchedStateByTarget({
            practice: practice.unmatchedState,
            patient: patient.unmatchedState
        });
        setSeeded(true);
    }, [seeded, defaults]);

    const setValues = useCallback(
        (values: ShipToFormValues) => {
            setFormsByTarget((prev) => ({ ...prev, [target]: values }));
        },
        [target]
    );

    return {
        target,
        setTarget,
        values: formsByTarget[target],
        setValues,
        unmatchedState: unmatchedStateByTarget[target]
    };
}
